/**
 * CounterPage.js
 *
 * /fly/counter — the check-in counter at the party's door, for the ground staff (staff key, remembered on the device).
 * Scan a guest's Wallet pass with the phone's camera or a USB/Bluetooth barcode scanner (or type their PNR,
 * or pick them from the list) to mark them as landed.
 * The arrivals board picks it up within a couple of seconds.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleAlert, DoorClosed, DoorOpen, LogOut, PlaneLanding, Search, Undo2 } from "lucide-react";
import toast from "react-hot-toast";
import KeyGate from "./components/KeyGate";
import QrScanner from "./components/QrScanner";
import { getCounterGuests, markArrived, setArrivalsClosed, undoArrival } from "./helpers/flyApi";
import { beep, formatTime, useBarcodeScanner, useNoIndex, useStoredKey } from "./helpers/flyPage";

const PAPER = "#FBF7EF";
const INK = "#1B2A41";
const MUTED = "#5B6577";
const LINE = "#C9C2B4";
const ERROR = "#A8183D";
const MONO = "'JetBrains Mono', monospace";

// How long a result stays on screen, and how long the same QR is ignored after being read
const RESULT_MS = 5000;
const SAME_CODE_MS = 6000;
// Keeps the counts and list in step with other counter phones
const REFRESH_MS = 10000;

/**
 * The big result after a scan: the guest in their colour, or an error
 * @param {Object} result       -   { guest, already } or { error }
 */
const ResultOverlay = ({ result, onDismiss, onUndo }) => {
    if (result.error) {
        return (
            <button type="button" onClick={onDismiss} className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: ERROR, color: PAPER }}>
                <CircleAlert size={64} aria-hidden="true" />
                <p className="text-3xl font-bold tracking-[0.1em]">NOT FOUND</p>
                <p className="text-lg">{result.error}</p>
                {result.code && <p className="text-sm opacity-80" style={{ fontFamily: MONO }}>{result.code}</p>}
                <p className="text-xs tracking-[0.2em] opacity-70 pt-6">TAP TO CLOSE</p>
            </button>
        );
    }
    const { guest, already } = result;
    return (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: guest.colour, color: PAPER }}>
            <button type="button" onClick={onDismiss} className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <PlaneLanding size={56} aria-hidden="true" />
                <p className="text-sm font-bold tracking-[0.3em] opacity-90">{already ? `ALREADY LANDED AT ${formatTime(guest.arrivedAt)}` : "LANDED"}</p>
                <p className="text-4xl sm:text-6xl font-bold tracking-[0.04em] leading-tight">{guest.fullName}</p>
                <p className="text-xl sm:text-2xl font-medium tracking-[0.12em] uppercase">from {guest.fromCity}{guest.country ? `, ${guest.country}` : ""}</p>
                <p className="text-lg tracking-[0.2em]" style={{ fontFamily: MONO }}>SEAT {guest.seat}</p>
                <p className="text-xs tracking-[0.2em] opacity-70 pt-6">TAP TO CLOSE</p>
            </button>
            {!already && (
                <button type="button" onClick={onUndo} className="m-4 inline-flex items-center justify-center gap-2 rounded-full border-2 px-6 py-3 text-sm font-bold tracking-[0.16em]" style={{ borderColor: PAPER }}>
                    <Undo2 size={16} aria-hidden="true" /> WRONG PERSON? UNDO
                </button>
            )}
        </div>
    );
};

const CounterPage = () => {
    const [key, saveKey, forgetKey] = useStoredKey("yj030-staff-key");
    const [keyError, setKeyError] = useState("");
    // { guests, landed, total, closed } once loaded
    const [data, setData] = useState(null);
    const [result, setResult] = useState(null);
    // The last check-in made on this phone, for Undo: { guest } (guest.arrivedAt identifies it)
    const [last, setLast] = useState(null);
    const [typedPnr, setTypedPnr] = useState("");
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const audioRef = useRef(null);
    const lastCodeRef = useRef({ code: "", at: 0 });
    // Scans waiting while one is being saved, so guests can be scanned one after another without any being dropped
    const busyRef = useRef(false);
    const queueRef = useRef([]);
    const resultTimerRef = useRef(null);

    useNoIndex("Check-in counter · Flight YJ030");

    const load = useCallback(async () => {
        if (!key) return;
        try {
            setData(await getCounterGuests(key));
            setKeyError("");
        } catch (err) {
            if (err.status === 401) {
                forgetKey();
                setKeyError("That key didn't work.");
            }
        }
    }, [key, forgetKey]);

    useEffect(() => {
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => clearInterval(timer);
    }, [load]);

    useEffect(() => () => clearTimeout(resultTimerRef.current), []);

    const unlockSound = () => {
        try {
            audioRef.current = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
            audioRef.current.resume();
        } catch {
            // No sound is fine
        }
    };

    const showResult = (newResult) => {
        setResult(newResult);
        clearTimeout(resultTimerRef.current);
        resultTimerRef.current = setTimeout(() => setResult(null), RESULT_MS);
        const ok = !newResult.error && !newResult.already;
        beep(audioRef.current, ok);
        navigator.vibrate?.(ok ? 120 : [80, 60, 80]);
    };

    const arrive = async (code) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        try {
            const response = await markArrived(key, code);
            setData((current) => current && {
                ...current,
                landed: response.landed,
                total: response.total,
                guests: current.guests.map((guest) => (guest.pnr === response.guest.pnr ? response.guest : guest)),
            });
            if (!response.already) setLast({ guest: response.guest });
            showResult(response);
        } catch (err) {
            if (err.status === 401) load();
            else showResult({ error: err.status === 404 ? "No passenger has this boarding pass." : (err.message || "Couldn't reach the server. Try again."), code });
        } finally {
            busyRef.current = false;
            setBusy(false);
            const next = queueRef.current.shift();
            if (next) arrive(next);
        }
    };

    // The camera reads the same QR many times a second: act on it once
    const handleScan = (code) => {
        const now = Date.now();
        if (result || (code === lastCodeRef.current.code && now - lastCodeRef.current.at < SAME_CODE_MS)) return;
        lastCodeRef.current = { code, at: now };
        arrive(code);
    };

    // A barcode scanner (USB or Bluetooth) on the laptop: a new scan replaces whatever result is showing, so guests can
    // be scanned one after another (queued if one is still saving); the same pass twice in a row is ignored
    useBarcodeScanner((code) => {
        const now = Date.now();
        if (code === lastCodeRef.current.code && now - lastCodeRef.current.at < SAME_CODE_MS) return;
        lastCodeRef.current = { code, at: now };
        unlockSound();
        setResult(null);
        if (busyRef.current) queueRef.current.push(code);
        else arrive(code);
    }, !!key);

    const handleTyped = (e) => {
        e.preventDefault();
        unlockSound();
        if (typedPnr.trim()) arrive(typedPnr.trim());
        setTypedPnr("");
    };

    const undo = async () => {
        if (!last) return;
        try {
            const counts = await undoArrival(key, last.guest.pnr, last.guest.arrivedAt);
            toast.success(`Undone: ${last.guest.fullName} is no longer landed`);
            setLast(null);
            setResult(null);
            setData((current) => current && {
                ...current,
                ...counts,
                guests: current.guests.map((guest) => (guest.pnr === last.guest.pnr ? { ...guest, arrivedAt: null } : guest)),
            });
        } catch (err) {
            toast.error(err.message || "Couldn't undo.");
        }
    };

    const toggleClosed = async () => {
        const closing = !data.closed;
        const message = closing
            ? "Close arrivals? Everyone who hasn't landed will show as DIVERTED on the board."
            : "Reopen arrivals? Guests who haven't landed go back to EXPECTED or DELAYED.";
        if (!window.confirm(message)) return;
        try {
            const { closed } = await setArrivalsClosed(key, closing);
            setData((current) => ({ ...current, closed }));
        } catch (err) {
            toast.error(err.message || "Couldn't change arrivals.");
        }
    };

    const waiting = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (data?.guests || [])
            .filter((guest) => !guest.arrivedAt)
            .filter((guest) => !query || `${guest.fullName} ${guest.pnr} ${guest.fromCity}`.toLowerCase().includes(query));
    }, [data, search]);

    if (!key || (!data && keyError)) {
        return (
            <div className="min-h-screen px-4" style={{ background: "#E9E4DA", color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                <KeyGate title="Check-in counter" label="Staff key" error={keyError} onSubmit={(value) => { setKeyError(""); saveKey(value); }} />
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full pb-16" style={{ background: "#E9E4DA", color: INK, fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
            <div className="mx-auto w-full max-w-xl px-4 pt-5 flex flex-col gap-5">
                <header className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[11px] font-bold tracking-[0.24em]" style={{ color: MUTED }}>RIGHT BROTHER AIRWAYS · YJ030 · BAH</p>
                        <h1 className="text-xl font-bold tracking-[0.14em]">CHECK-IN COUNTER</h1>
                    </div>
                    <button type="button" onClick={forgetKey} className="inline-flex items-center gap-1 text-xs underline underline-offset-2" style={{ color: MUTED }}>
                        <LogOut size={12} aria-hidden="true" /> Lock
                    </button>
                </header>

                {!data ? (
                    <p className="text-center text-sm font-bold tracking-[0.24em] animate-pulse" style={{ color: MUTED }}>LOADING…</p>
                ) : (
                    <>
                        <div className="rounded-2xl px-5 py-4 flex items-baseline justify-between" style={{ background: INK, color: PAPER }}>
                            <p className="text-3xl font-bold" style={{ fontFamily: MONO }}>
                                {data.landed} <span className="text-lg opacity-70">of {data.total}</span>
                            </p>
                            <p className="text-sm font-bold tracking-[0.24em]">{data.closed ? "ARRIVALS CLOSED" : "LANDED"}</p>
                        </div>

                        <QrScanner onCode={handleScan} paused={busy || !!result} onStart={unlockSound} />
                        <p className="text-xs text-center -mt-2" style={{ color: MUTED }}>
                            Using a barcode scanner? Just scan the pass. No need to tap anything first.
                        </p>

                        {last && (
                            <button type="button" onClick={undo} className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-bold tracking-[0.12em]" style={{ borderColor: ERROR, color: ERROR }}>
                                <Undo2 size={16} aria-hidden="true" /> UNDO LAST: {last.guest.fullName.toUpperCase()}
                            </button>
                        )}

                        <form onSubmit={handleTyped} className="flex gap-2">
                            <input
                                type="text"
                                value={typedPnr}
                                // Kept exactly as typed: a scanned pass serial is case-sensitive (the backend matches PNRs in any case).
                                // It only looks capitalised.
                                onChange={(e) => setTypedPnr(e.target.value)}
                                placeholder="Type a PNR"
                                aria-label="PNR or pass code"
                                maxLength={20}
                                autoCapitalize="characters"
                                autoCorrect="off"
                                autoComplete="off"
                                spellCheck={false}
                                className="flex-1 min-w-0 rounded-lg border px-3 py-2.5 bg-white text-base tracking-[0.3em] font-bold uppercase placeholder:normal-case focus:outline-none focus:ring-2"
                                style={{ borderColor: LINE, fontFamily: MONO, "--tw-ring-color": INK }}
                            />
                            <button type="submit" disabled={busy} className="rounded-lg px-5 text-sm font-bold tracking-[0.14em] disabled:opacity-50" style={{ background: INK, color: PAPER }}>
                                LAND
                            </button>
                        </form>

                        <section className="rounded-2xl px-4 py-4 flex flex-col gap-3" style={{ background: PAPER }}>
                            <p className="text-[11px] font-bold tracking-[0.18em]" style={{ color: MUTED }}>NO PASS? STILL TO LAND · {data.total - data.landed}</p>
                            <label className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-white" style={{ borderColor: LINE }}>
                                <Search size={16} color={MUTED} aria-hidden="true" />
                                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, city or PNR" aria-label="Search guests" className="flex-1 min-w-0 bg-transparent text-base focus:outline-none" />
                            </label>
                            <ul className="flex flex-col">
                                {waiting.length === 0 && <li className="py-2 text-sm" style={{ color: MUTED }}>{search ? "Nobody matches." : "Everyone has landed."}</li>}
                                {waiting.map((guest) => (
                                    <li key={guest.pnr} style={{ borderBottom: "1px solid #E2DCCF" }}>
                                        <button
                                            type="button"
                                            onClick={() => { unlockSound(); if (window.confirm(`Check in ${guest.fullName}?`)) arrive(guest.pnr); }}
                                            className="w-full flex items-center gap-3 py-3 text-left"
                                        >
                                            <span className="h-8 w-1.5 rounded-full shrink-0" style={{ background: guest.colour }} aria-hidden="true" />
                                            <span className="flex-1 min-w-0">
                                                <span className="block font-medium truncate">{guest.fullName}</span>
                                                <span className="block text-xs" style={{ color: MUTED }}>{guest.fromCity} · Seat {guest.seat}</span>
                                            </span>
                                            <span className="text-xs font-bold" style={{ fontFamily: MONO, color: MUTED }}>{guest.pnr}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <button type="button" onClick={toggleClosed} className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-6 py-3 text-sm font-bold tracking-[0.16em]" style={{ borderColor: INK }}>
                            {data.closed
                                ? <><DoorOpen size={16} aria-hidden="true" /> REOPEN ARRIVALS</>
                                : <><DoorClosed size={16} aria-hidden="true" /> CLOSE ARRIVALS</>}
                        </button>
                    </>
                )}
            </div>

            {result && <ResultOverlay result={result} onDismiss={() => setResult(null)} onUndo={undo} />}
        </div>
    );
};

export default CounterPage;
